import React, { FC, useEffect, useMemo, useState } from 'react';
import { ResponseType } from 'src/models/bids/BidTask/types';
import YesNoAnswer from './YesNo';
import FreeTextAnswer from './FreeText';
import FileAnswer from './File';
import { Form } from 'src/common';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import AnswerTopBar from './AnswerTopBar';
import classNames from 'classnames';
import { useTask } from 'src/models/bids/BidTask/hooks';
import { ContentLibraryProvider } from 'src/models/bids/ContentLibrary/providers';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import ContentLibrary from '../ContentLibrary';

export interface IAnswerFormState {
  yesNo?: boolean | null;
  freeText: string;
}

export const BidTaskAnswer: FC = () => {
  const { t } = useTranslation();
  const isBiddingGPTFeature = useFeatureFlag(FeatureFlag.BiddingTool_BiddingGPT);
  const { data: task } = useTask();
  const [isFocused, setIsFocused] = useState(false);
  const [form] = Form.useForm<IAnswerFormState>();

  useEffect(() => {
    form.setFieldsValue({
      yesNo: task?.answer ? task.answer.answer : undefined,
      freeText: task?.answer?.content || ''
    });
  }, [form, task]);

  const initialValues: IAnswerFormState = useMemo(() => {
    return {
      yesNo: task?.answer ? task.answer.answer : undefined,
      freeText: task?.answer?.content || ''
    };
  }, [task]);

  const responseType = task?.responseType;
  const answerTitle = useMemo(() => {
    return responseType
      ? responseType === ResponseType.File
        ? t('Common.listOfAttachments')
        : t(`BiddingTool.ResponseType.${responseType}`)
      : undefined;
  }, [responseType, t]);

  return task ? (
    <>
      <div className={styles.container}>
        <Form form={form} layout={'vertical'} initialValues={initialValues} className={styles.unitContent}>
          <AnswerTopBar />
          {!!responseType ? (
            <>
              <div className={styles.title}>{answerTitle}</div>
              {responseType === ResponseType.YesNo && <YesNoAnswer form={form} />}
              {responseType === ResponseType.File && <FileAnswer />}
              {responseType === ResponseType.FreeText && (
                <FreeTextAnswer form={form} isFocused={isFocused} setIsFocused={setIsFocused} />
              )}
              {responseType === ResponseType.FreeYesNo && (
                <>
                  <YesNoAnswer form={form} />
                  <FreeTextAnswer form={form} isFocused={isFocused} setIsFocused={setIsFocused} />
                </>
              )}
              {responseType === ResponseType.Multiple && (
                <>
                  <YesNoAnswer form={form} />
                  <FreeTextAnswer form={form} isFocused={isFocused} setIsFocused={setIsFocused} />
                  <FileAnswer />
                </>
              )}
            </>
          ) : (
            <p className={styles.desc}>{t('BiddingTool.addResponse')}</p>
          )}
        </Form>
        {task.isGenerated && (
          <div className={classNames(styles.unit, styles.prePopulatedMarker)}>
            {t('BiddingTool.taskWasPrePopulate')}
          </div>
        )}
      </div>
      {isBiddingGPTFeature && task && task.content && (
        <ContentLibraryProvider question={task.content}>
          <ContentLibrary form={form} setIsFocused={setIsFocused} />
        </ContentLibraryProvider>
      )}
    </>
  ) : null;
};

export default BidTaskAnswer;
